// Events
import { SEARCH_QUERY, SEARCH_RESULT, SEARCH_RESULTS } from 'events';

// Utils
import waitToLoad from 'utils/waitToLoad';

// Helpers
import { createAutocomplete, createCustomHits, createCustomSearchBox } from './widgets';

const createSearch = (ctx) => {
  const { appId, apiKey, indices, algoliaQuerySuggestionsIndex } = window.theme.algoliaSettings;
  const { productsIndex, collectionsIndex, pagesIndex, articlesIndex } = indices;

  const { algoliasearch, instantsearch } = window;

  // Widgets
  const { configure, index } = instantsearch.widgets;

  const { Algolia: allowed } = window.theme.consent || { Algolia: false };

  const client = algoliasearch(appId, apiKey);
  const searchClient = {
    ...client,
    search(requests) {
      if (requests.every(({ params }) => !params.query)) {
        return Promise.resolve({
          results: requests.map(() => ({
            hits: [],
            nbHits: 0,
            nbPages: 0,
            page: 0,
            processingTimeMS: 0,
            hitsPerPage: 0,
            exhaustiveNbHits: false,
            query: '',
            params: '',
          })),
        });
      }

      return client.search(requests);
    },
  };
  const search = instantsearch({
    indexName: productsIndex,
    searchClient,
    insights: allowed,
    insightsInitParams: {
      useCookie: false,
    },
  });

  // Search box
  const customSearchBox = createCustomSearchBox(ctx);

  // Hits
  const customProductHits = createCustomHits(ctx, 'products');
  const customCollectionHits = createCustomHits(ctx, 'collections');
  const customPageHits = createCustomHits(ctx, 'pages');
  const customArticleHits = createCustomHits(ctx, 'articles');

  const autocomplete = createAutocomplete(ctx);

  search.addWidgets([
    configure({ hitsPerPage: 4, filters: 'NOT meta.askphill.search_result_behaviour:hidden' }),
    customSearchBox(),
    customProductHits(),
    index({ indexName: collectionsIndex }).addWidgets([
      configure({ hitsPerPage: 3, filters: 'NOT meta.askphill.search_result_behaviour:hidden' }),
      customCollectionHits(),
    ]),
    index({ indexName: pagesIndex }).addWidgets([
      configure({ hitsPerPage: 3, filters: 'NOT meta.askphill.search_result_behaviour:hidden' }),
      customPageHits(),
    ]),
    index({ indexName: articlesIndex }).addWidgets([
      configure({ hitsPerPage: 3, filters: 'NOT meta.askphill.search_result_behaviour:hidden' }),
      customArticleHits(),
    ]),
    index({ indexName: algoliaQuerySuggestionsIndex }).addWidgets([
      autocomplete({
        container: document.querySelector('[data-search-suggestions-list]'),
      }),
    ]),
  ]);

  let accumulatedResults = {
    products: null,
    collections: null,
    pages: null,
    articles: null,
  };

  ctx.on(SEARCH_QUERY, () => {
    accumulatedResults = {
      products: null,
      collections: null,
      pages: null,
      articles: null,
    };
  });

  ctx.on(SEARCH_RESULT, (state, [type, results]) => {
    accumulatedResults[type] = results;

    const collectedAllResults = Object.keys(accumulatedResults).every(
      (key) => accumulatedResults[key] !== null,
    );

    if (collectedAllResults) {
      ctx.emit(SEARCH_RESULTS, {
        search: {
          ...state.search,
          results: {
            ...accumulatedResults,
          },
        },
      });
    }
  });

  return [client, search];
};

export default class {
  constructor(ctx) {
    this.ctx = ctx;
  }

  async start() {
    try {
      await waitToLoad('AlgoliaSearch');

      [this.client, this.search] = createSearch(this.ctx);

      this.search.start();
    } catch (err) {
      // eslint-disable-next-line
      console.error(err);
    }
  }
}
